import React, { useState, useCallback, useMemo } from 'react';
import {
  CheckSquare,
  Plus,
  Calendar,
  User,
  Loader2,
  MessageSquare,
  ClipboardList
} from 'lucide-react';
import { useProjectTasks } from '../hooks/useProjectTasks';
import { ScheduleTaskModal } from '../modals/ScheduleTaskModal';
import { AddWorkUpdateModal } from '../modals/AddWorkUpdateModal';
import { TaskWorkUpdatesListModal } from '../modals/TaskWorkUpdatesListModal';
import { hasProjectInstallationTeam, hasMaterialReceiptForTaskAssignment } from '../utils/projectTeamAccess';
import { formatInstallationTrade } from '../../verve-teams/utils/teamMemberLabels';

const StatusBadge = ({ status }) => {
  const styles = {
    PENDING: 'bg-amber-50 text-amber-600 border-amber-100/50',
    STARTED: 'bg-blue-50 text-blue-600 border-blue-100/50',
    IN_PROGRESS: 'bg-blue-50 text-blue-600 border-blue-100/50',
    ON_HOLD: 'bg-rose-50 text-rose-600 border-rose-100/50',
    WAITING_MATERIAL: 'bg-blue-50 text-blue-600 border-blue-100/50',
    COMPLETED: 'bg-emerald-50 text-emerald-600 border-emerald-100/50',
    CANCELLED: 'bg-slate-50 text-slate-400 border-slate-100/50'
  };

  return (
    <span className={`px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-tight border ${styles[status] || styles.PENDING}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

export const ProjectTasksTab = ({ project, onProjectUpdated, tasksInventoryLocked = false, hideHeader = false }) => {
  // Polinaidu: 24-04-2026 (17:35) - Commented out original local state as per request
  /*
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workUpdateTask, setWorkUpdateTask] = useState(null);
  const [listTask, setListTask] = useState(null);

  const currentUser = useMemo(() => {
    try {
      const userStr = localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : {};
    } catch {
      return {};
    }
  }, []);

  const canScheduleTasks = useMemo(() => {
    const role = String(currentUser.role || '').toLowerCase();
    return ['owner', 'admin', 'designer'].includes(role);
  }, [currentUser.role]);

  const isStaff = canScheduleTasks;

  const isTaskAssignee = (task) => {
    const uid = currentUser._id || currentUser.id;
    const aid = task.assignedOfficer?._id || task.assignedOfficer?.id || task.assignedOfficer;
    return Boolean(aid && uid && String(aid) === String(uid));
  };

  const showAddWorkUpdateForTask = (task) => {
    const role = String(currentUser.role || '').toLowerCase();
    const isStaff = ['owner', 'admin', 'designer'].includes(role);
    if (task.status === 'COMPLETED') return false;
    if (isStaff) return true;
    return role === 'installation_team' && isTaskAssignee(task);
  };

  const showViewUpdatesForTask = (task) => {
    if (isStaff) return true;
    if (currentUser.role === 'installation_team' && isTaskAssignee(task)) return task.status === 'COMPLETED';
    return false;
  };

  const canPostWorkUpdate = (task) => {
    if (canScheduleTasks) return true;
    const aid = task.assignedOfficer?._id || task.assignedOfficer;
    if (currentUser.role === 'installation_team' && aid && String(aid) === String(currentUser._id)) {
      return task.status !== 'COMPLETED';
    }
    return false;
  };

  const { tasks, loading, refreshTasks } = useProjectTasks(project._id);

  /*
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await taskService.getProjectTasks(project._id);
      setTasks(data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [project._id]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);
  */

  // Polinaidu: 24-04-2026 (17:18) - Refactored to use useProjectTasks hook
  const afterWorkUpdate = useCallback(async () => {
    await refreshTasks();
    if (onProjectUpdated) await onProjectUpdated();
  }, [refreshTasks, onProjectUpdated]);

  const formatDate = (dateString) => {
    if (!dateString) return 'n/a';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isAddDisabled =
    tasksInventoryLocked ||
    !hasProjectInstallationTeam(project) ||
    !hasMaterialReceiptForTaskAssignment(project);

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-500">
      {/* Header Section */}
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center border border-slate-100">
              <CheckSquare className="w-5 h-5 text-[#2b448f]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 tracking-tight capitalize">Project execution tasks</h2>
              <p className="text-[11px] font-bold text-slate-400 tracking-tight">Manage and track site execution progress</p>
            </div>
          </div>
          {isStaff && (
          <button 
            onClick={() => !isAddDisabled && setIsModalOpen(true)}
            disabled={isAddDisabled}
            title={
              tasksInventoryLocked
                ? 'Actions are locked after signed documents were uploaded on the Documents tab'
                : !hasProjectInstallationTeam(project)
                  ? 'Assign at least one installation member on Overview before adding tasks'
                  : !hasMaterialReceiptForTaskAssignment(project)
                    ? 'At least one installation member must confirm material receipt on this project before tasks can be assigned'
                    : 'Add new task'
            }
            className={`px-6 py-2.5 rounded-lg text-[11px] font-bold capitalize flex items-center gap-2 transition-all shadow-none border border-transparent ${
              isAddDisabled 
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200' 
                : 'bg-[#2b448f] text-white hover:bg-[#1a2d5f]'
            }`}
          >
            <Plus className="w-4 h-4" />
            Add task
          </button>
          )}
        </div>
      )}

      {/* Tasks List */}
      <div className="flex flex-col gap-5">
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-100 p-20 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-8 h-8 text-[#2b448f] animate-spin" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Syncing task pulse...</span>
          </div>
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 p-20 flex flex-col items-center justify-center text-center gap-5">
             <div className="w-20 h-20 rounded-3xl bg-slate-50 flex items-center justify-center border border-slate-50">
               <CheckSquare className="w-10 h-10 text-slate-200" />
             </div>
             <div className="flex flex-col gap-2">
               <h3 className="text-base font-black text-slate-800 tracking-tight capitalize">No tasks scheduled</h3>
               <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">Start by creating your first execution task</p>
             </div>
          </div>
        ) : (
          tasks.map((task) => (
            <div key={task._id} className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:border-slate-200 transition-all flex flex-col group animate-in slide-in-from-bottom-2 duration-300 shadow-none">
               <div className="p-6 flex flex-col gap-5">
                  {/* Top Row: Title & Status */}
                  <div className="flex items-start justify-between">
                     <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                           <h3 className="text-base font-extrabold text-slate-800 tracking-tight lowercase first-letter:uppercase">
                              {task.taskName}
                           </h3>
                           <StatusBadge status={task.status} />
                        </div>
                        <p className="text-[11px] font-medium text-slate-500 leading-relaxed max-w-2xl lowercase first-letter:uppercase">
                           {task.description || 'No description provided for this execution task.'}
                        </p>
                     </div>
                     <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-slate-800">{task.progress || 0}%</span>
                     </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full h-1.5 bg-slate-50 rounded-full overflow-hidden border border-slate-100/50">
                     <div 
                        className="h-full bg-[#2b448f] rounded-full transition-all duration-1000"
                        style={{ width: `${task.progress || 0}%` }}
                     />
                  </div>

                  {/* Metadata Row */}
                  <div className="flex items-center justify-between">
                     <div className="flex items-center gap-8">
                        {/* Dates */}
                        <div className="flex items-center gap-6">
                           <div className="flex items-center gap-2">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              <div className="flex flex-col">
                                 <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Start</span>
                                 <span className="text-[10px] font-bold text-slate-700">{formatDate(task.startDate)}</span>
                              </div>
                           </div>
                           <div className="flex items-center gap-2 border-l border-slate-100 pl-6">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              <div className="flex flex-col">
                                 <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">End</span>
                                 <span className="text-[10px] font-bold text-slate-700">{formatDate(task.estimatedEndDate)}</span>
                              </div>
                           </div>
                        </div>

                        {/* Assigned To */}
                        <div className="flex items-center gap-2 border-l border-slate-100 pl-8">
                           <div className="w-8 h-8 rounded-lg bg-blue-50/50 flex items-center justify-center">
                              <User className="w-4 h-4 text-[#2b448f]" />
                           </div>
                           <div className="flex flex-col">
                              <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">Assigned officer</span>
                              <span className="text-[10px] font-bold text-slate-700">{task.assignedOfficer?.name || 'Installation member'}</span>
                              {(task.installationTrade || task.assignedOfficer?.installationTrade) && (
                                <span className="text-[9px] font-semibold text-slate-500">
                                  {formatInstallationTrade(task.installationTrade || task.assignedOfficer?.installationTrade)}
                                </span>
                              )}
                           </div>
                        </div>
                     </div>

                     {/* Action Buttons: staff = view only; field officer = add update OR view when completed */}
                     <div className="flex items-center gap-2 flex-wrap justify-end">
                        {showAddWorkUpdateForTask(task) && (
                          <button
                            type="button"
                            id={`task-add-update-${task._id}`}
                            onClick={() => !tasksInventoryLocked && setWorkUpdateTask(task)}
                            disabled={tasksInventoryLocked}
                            title={
                              tasksInventoryLocked
                                ? 'Locked after signed documents were uploaded on the Documents tab'
                                : undefined
                            }
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ease-in-out h-9 shadow-sm ${
                              tasksInventoryLocked
                                ? 'bg-slate-50 border border-slate-200 text-slate-300 cursor-not-allowed'
                                : 'bg-[#64748b] text-white hover:bg-[#475569] hover:scale-105 active:scale-95'
                            }`}
                          >
                            <ClipboardList className="w-4 h-4 shrink-0" />
                            <span className="text-[11px] font-bold capitalize">Add work update</span>
                          </button>
                        )}
                        {showViewUpdatesForTask(task) && (
                          <button
                            type="button"
                            id={`task-view-updates-${task._id}`}
                            onClick={() => setListTask(task)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-100 text-slate-600 hover:bg-slate-100 transition-all duration-300 ease-in-out h-10"
                          >
                            <MessageSquare className="w-[18px] h-[18px] shrink-0" />
                            <span className="text-[11px] font-semibold capitalize">View updates</span>
                          </button>
                        )}
                     </div>
                  </div>
               </div>
            </div>
          ))
        )}
      </div>

      <ScheduleTaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        project={project}
        onSuccess={refreshTasks}
      />

      <AddWorkUpdateModal
        isOpen={Boolean(workUpdateTask)}
        onClose={() => setWorkUpdateTask(null)}
        task={workUpdateTask}
        onSuccess={afterWorkUpdate}
      />

      <TaskWorkUpdatesListModal
        isOpen={Boolean(listTask)}
        onClose={() => setListTask(null)}
        task={listTask}
      />
    </div>
  );
};
